import { get, isEmpty } from 'lodash';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import moment from 'moment';
import { useAtom } from 'jotai';
import { enableTableCompare, setSlice } from '@store/trends/birdChart/actions';
import {
  initMetricFilter,
  removeMetricFilter,
  setDimensionPanelTab,
} from '@store/trends/base/actions';
import { metricFilterLookupSelector } from '@store/trends/base/selectors';
import {
  appMetricFilteringSelector,
  filterSelector,
  realTimeModeOnTrendsSelector,
} from '@store/common/selectors';
import { getDistributionMetrics } from '@components/metrics-panel-hz/utils/metricsMapping';
import { isAppModule } from '@utils/constants';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import {
  findDimensionDetailsInFilter,
  getFilterModeFromDimensionTypes,
} from '@src/utils/filter/getDimensionTypesFromFilter';
import { DIMENSION_TYPES_CONSTANTS } from '@utils/metrics';
import { Tabs } from '@store/trends/base/reducer';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { sliceAreaAtom } from '../atoms';
import { CHART_TYPE } from './utils';
import { METRIC_FILTER_TYPES, buildMetricFilter } from '../utils/metricFilterUtils';
import {
  tableCompareEnabledSelector,
  tableCompareIntervalSelector,
} from '@store/trends/birdChart/selectors';
import { ECO_PRESET_KEY } from '@src/components/common/Dashboard/UseCases/limitation';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';

const getMarkArea = {
  timeseries: (area) => {
    if (!isEmpty(area)) {
      return {
        data: [
          [{ xAxis: moment(area[0]).toISOString() }, { xAxis: moment(area[1]).toISOString() }],
        ],
      };
    }
    return [];
  },
  distribution: (area) => {
    if (!isEmpty(area)) {
      return {
        data: [[{ xAxis: area[0] }, { xAxis: area[1] }]],
      };
    }
    return [];
  },
};

function useSelect({ instance, chartMode, enabled, metric }) {
  const dispatch = useDispatch();
  const metricFilterLookup = useSelector(metricFilterLookupSelector);
  const selectedMetric = useSelector(selectedMetricSelector);
  const liveMode = useSelector(realTimeModeOnTrendsSelector);
  const appMetricFilteringFeature = useSelector(appMetricFilteringSelector);
  const globalFilter = useSelector(filterSelector);
  const dimensionByTypes = useSelector(dimensionByTypesSelector);
  const distributionMetrics = getDistributionMetrics();
  const { usecaseKey } = useSelector(activeDashboardSelector);

  const isTableCompareEnabled = useSelector(tableCompareEnabledSelector);
  const tableCompareInterval = useSelector(tableCompareIntervalSelector);

  const [sliceArea, setSliceArea] = useAtom(sliceAreaAtom);

  const addMarkAreaForTableCompareInterval = (tableCompareInterval) => {
    const itemStyle = {
      color: 'rgba(60, 60, 60, 0.2)',
    };

    tableCompareInterval[0].itemStyle = itemStyle;
    return tableCompareInterval;
  };

  const handleTableInterval = useCallback(
    (area) => {
      if (instance) {
        try {
          const { series, _viewType = '' } = instance.getOption();
          if (_viewType in getMarkArea) {
            const compareArea = getMarkArea[_viewType](area);
            const compareMarkArea = addMarkAreaForTableCompareInterval(compareArea.data[0]);
            series[0].markArea.data[1] = compareMarkArea;
            instance.setOption({ series }, { replaceMerge: 'series' });
          }
        } catch (err) {
          // ignore
        }
      }
    },
    [instance],
  );

  // handle chart slice action
  const handleSelect = useCallback(
    (area) => {
      if (instance) {
        try {
          const { series, _viewType = '' } = instance.getOption();
          if (_viewType in getMarkArea) {
            const markArea = getMarkArea[_viewType](area);
            series[0].markArea = markArea;
            instance.setOption({ series }, { replaceMerge: 'series' });
          }
        } catch (err) {
          // ignore
        }
      }
    },
    [instance],
  );

  const handleBrushEnd = useCallback(
    (burshData) => {
      const option = instance.getOption();
      const metric = option._chartMetric;

      const area = get(burshData, ['areas'], []);
      const range = get(area, ['0', 'coordRange'], null);

      if (option._viewType === 'timeseries') {
        setSliceArea(range);
        // time slice on dimension table
        // set or clear range
        dispatch(setSlice([0, 100], range || [0, 0]));
      } else if (option._viewType === 'distribution') {
        const distributionConfig = option._distributionConfig;

        // Feature flag for app metric filtering
        if (isAppModule) {
          if (appMetricFilteringFeature) {
            const { dimensionTypes } = findDimensionDetailsInFilter(globalFilter, dimensionByTypes);
            const filterMode = getFilterModeFromDimensionTypes(dimensionTypes);
            if (filterMode === DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata) {
              message.warning(
                'Secondary filter is not supported when global filter has dimension with type eventTagKeyValueMetadata',
              );
              return;
            }
            if (usecaseKey == ECO_PRESET_KEY.APP_NETWORK_PERFORMANCE) {
              message.warning('Secondary filter not supported');
              return;
            }
            /** HP-4838: open Network Request tab when secondary filter by AverageRequestDuration */
            if (selectedMetric === 'AverageRequestDuration') {
              dispatch(setDimensionPanelTab(Tabs.AppAnalyseNetworkRequest));
            }
          } else {
            return;
          }
        }

        if (liveMode) {
          message.warning('Secondary filtering is not supported while on real time mode');
          return;
        }

        if (isTableCompareEnabled) {
          dispatch(enableTableCompare(false));
        }

        if (!Object.keys(distributionMetrics).includes(metric)) {
          message.warning('Secondary filtering is not supported by the selected metric');
          return;
        }

        // update metric filter
        const data = get(option, ['series', 0, 'data'], null);

        if (data && range) {
          const metricFilter = buildMetricFilter({ metric, data, range, distributionConfig });
          if (metricFilter) {
            dispatch(initMetricFilter(metricFilter));
          } else {
            message.warning('Invalid selection. Cannot apply a metric filter.');
            return;
          }
        } else {
          // removing bound type metric filter
          dispatch(removeMetricFilter({ metric, meta: { type: METRIC_FILTER_TYPES.bound } }));
        }
      }

      // clear brush selection
      instance.dispatchAction({
        type: 'brush',
        command: 'clear',
        areas: [],
      });
    },
    [
      appMetricFilteringFeature,
      dimensionByTypes,
      dispatch,
      globalFilter,
      instance,
      liveMode,
      selectedMetric,
      setSliceArea,
      isTableCompareEnabled,
    ],
  );

  // register chart events
  useEffect(() => {
    if (instance && enabled) {
      instance.on('brushEnd', handleBrushEnd);
      return () => {
        instance.off('brushEnd', handleBrushEnd);
      };
    }
  }, [instance, enabled, handleBrushEnd]);

  // persist slice for timeseries
  useEffect(() => {
    if (sliceArea && [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode)) {
      handleSelect(sliceArea);
    }
  }, [instance, chartMode, handleSelect, sliceArea]);

  // persist slice for timeseries
  useEffect(() => {
    if (sliceArea && [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode)) {
      if (isTableCompareEnabled && tableCompareInterval) {
        handleTableInterval([tableCompareInterval.from, tableCompareInterval.to]);
      } else {
        handleSelect(sliceArea);
      }
    }
  }, [instance, chartMode, handleSelect, sliceArea, isTableCompareEnabled, tableCompareInterval]);

  // persist slice for metric filter
  useEffect(() => {
    if ([CHART_TYPE.DISTRIBUTION].includes(chartMode)) {
      const range = get(metricFilterLookup, [metric, 'meta', 'range'], []);
      handleSelect(range);
    }
  }, [instance, chartMode, metric, metricFilterLookup]);
}

export default useSelect;
